import { TradeDirection } from "@atlas-int/data/types";
import { worldGroupDatum, UIView } from "@atlas-int/data";

/**
 * Infers trade direction from legacy URL parameters (before explicit tradeDirection param)
 * Used for backward compatibility with old bookmarked/shared URLs
 */
export function inferTradeDirectionFromLegacyParams({
  exporter,
  importer,
  view,
}: {
  exporter: string;
  importer: string;
  view: string | null;
}): TradeDirection {
  const isWorldExporter = exporter === worldGroupDatum.groupId;
  const isWorldImporter = importer === worldGroupDatum.groupId;

  if (view === UIView.Markets) {
    // Markets view: showing location partners
    if (isWorldExporter && !isWorldImporter) {
      // World exports to importer = viewing who importer imports from
      return TradeDirection.Imports;
    } else if (isWorldImporter && !isWorldExporter) {
      // Exporter exports to world = viewing who exporter exports to
      return TradeDirection.Exports;
    } else if (isWorldExporter && isWorldImporter) {
      // Both world - default to exports
      return TradeDirection.Exports;
    }
  } else if (view === UIView.Products) {
    // Products view: showing products traded
    if (isWorldImporter && !isWorldExporter) {
      // World imports from exporter = what does exporter export?
      return TradeDirection.Exports;
    } else if (isWorldExporter && !isWorldImporter) {
      // Exporter is world, importer is specific = what does importer import?
      return TradeDirection.Imports;
    }
  }

  // Default fallback
  return TradeDirection.Exports;
}

/**
 * Migrates old-style URLs (without tradeDirection parameter) to new format
 * Normalizes empty strings to world group ID and adds explicit tradeDirection
 */
export function migrateOldUrlToNewFormat(params: URLSearchParams): {
  needsMigration: boolean;
  migratedParams: URLSearchParams;
} {
  // If tradeDirection already exists, no migration needed
  if (params.has("tradeDirection")) {
    return { needsMigration: false, migratedParams: params };
  }

  // Only migrate if there are actual parameters that need converting
  // If no exporter/importer params exist at all, let default settings handle it
  const hasExporter = params.has("exporter");
  const hasImporter = params.has("importer");

  if (!hasExporter && !hasImporter) {
    // No location parameters at all - this is a fresh URL, let defaults work
    return { needsMigration: false, migratedParams: params };
  }

  const exporter = params.get("exporter") || "";
  const importer = params.get("importer") || "";
  const view = params.get("view");

  // Check if there are empty strings that need normalizing
  const hasEmptyStrings = exporter === "" || importer === "";

  // Infer tradeDirection from the exporter/importer pattern
  // Use normalized values for inference (treat empty strings as world)
  const normalizedExporter =
    exporter === "" ? worldGroupDatum.groupId : exporter;
  const normalizedImporter =
    importer === "" ? worldGroupDatum.groupId : importer;

  const tradeDirection = inferTradeDirectionFromLegacyParams({
    exporter: normalizedExporter,
    importer: normalizedImporter,
    view,
  });

  // Create new params with tradeDirection
  const newParams = new URLSearchParams(params);

  // Only set tradeDirection if it's NOT the default (exports)
  // This allows use-query-params to remove default values from URL
  if (tradeDirection !== TradeDirection.Exports) {
    newParams.set("tradeDirection", tradeDirection);
  }

  // Only update if they were actually empty strings
  if (exporter === "") {
    newParams.set("exporter", normalizedExporter);
  }
  if (importer === "") {
    newParams.set("importer", normalizedImporter);
  }

  // Check if any actual changes were made
  const needsMigration =
    hasEmptyStrings || tradeDirection !== TradeDirection.Exports;

  return { needsMigration, migratedParams: newParams };
}
